import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../utils/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { Plus, Trash2, Search, Wrench, Printer } from "lucide-react";
import { SearchableSelect } from "../components/ui/searchable-select";



const ServicePurchases = () => {
  const [servicePurchases, setServicePurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    supplier_id: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    amount: "",
    notes: "",
    payment_method: "cash",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [spRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/service-purchases`),
        axios.get(`${API}/suppliers`),
      ]);
      setServicePurchases(spRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplier_id || !formData.description || formData.amount <= 0) {
      toast.error("Tüm zorunlu alanları doldurun");
      return;
    }

    try {
      await axios.post(`${API}/service-purchases`, formData);
      toast.success("Hizmet alışı kaydedildi");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Failed to save:", error);
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu hizmet alışını silmek istediğinizden emin misiniz?")) return;

    try {
      await axios.delete(`${API}/service-purchases/${id}`);
      toast.success("Hizmet alışı silindi");
      fetchData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      amount: "",
      notes: "",
      payment_method: "cash",
    });
  };

  const filteredSP = servicePurchases.filter(
    (sp) =>
      sp.supplier_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sp.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sp.invoice_no.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 print:space-y-4" data-testid="service-purchases-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Hizmet Alış
          </h1>
          <p className="text-slate-500 mt-1">Hizmet alış kayıtlarını yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handlePrint} className="no-print">
            <Printer className="h-4 w-4 mr-2" /> Yazdır
          </Button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-service-purchase-btn">
              <Plus className="h-5 w-5 mr-2" />
              Yeni Hizmet Alışı
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Yeni Hizmet Alışı</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Tedarikçi *</Label>
                <SearchableSelect
                  options={suppliers.map((s) => ({
                    value: s.id,
                    label: s.name,
                    subtitle: s.phone || s.tax_number,
                  }))}
                  value={formData.supplier_id}
                  onChange={(value) => setFormData({ ...formData, supplier_id: value })}
                  placeholder="Tedarikçi seçin"
                  searchPlaceholder="Tedarikçi ara..."
                  emptyMessage="Tedarikçi bulunamadı."
                  data-testid="sp-supplier-select"
                />
              </div>
              <div className="space-y-2">
                <Label>Tarih</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  data-testid="sp-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Hizmet Açıklaması *</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Alınan hizmetin açıklaması"
                  rows={2}
                  data-testid="sp-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Tutar *</Label>
                <CurrencyInput
                  value={formData.amount}
                  onChange={(val) => setFormData({ ...formData, amount: val })}
                  placeholder="Tutar girin"
                  data-testid="sp-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Ödeme Yöntemi</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({ ...formData, payment_method: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Nakit</SelectItem>
                    <SelectItem value="bank">Banka</SelectItem>
                    <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                    <SelectItem value="deferred">Vadeli</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Notlar</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Ek notlar"
                  rows={2}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  İptal
                </Button>
                <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="sp-save-btn">
                  Kaydet
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Hizmet alışı ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0 md:p-0 print:p-0">
          {filteredSP.length > 0 ? (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-100">
                {filteredSP.map((sp) => (
                  <div key={sp.id} className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-slate-900">{sp.supplier_name}</p>
                        <p className="text-xs font-mono text-slate-500">{sp.invoice_no}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(sp.id)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-slate-600 mb-2 line-clamp-2">{sp.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">{sp.date}</span>
                      <span className="font-semibold text-emerald-600 tabular-nums">
                        ₺{sp.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="font-semibold">Belge No</TableHead>
                      <TableHead className="font-semibold">Tedarikçi</TableHead>
                      <TableHead className="font-semibold">Açıklama</TableHead>
                      <TableHead className="font-semibold">Tarih</TableHead>
                      <TableHead className="font-semibold text-right">Tutar</TableHead>
                      <TableHead className="font-semibold text-right">İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSP.map((sp) => (
                      <TableRow key={sp.id}>
                        <TableCell className="font-mono text-sm">{sp.invoice_no}</TableCell>
                        <TableCell className="font-medium">{sp.supplier_name}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{sp.description}</TableCell>
                        <TableCell>{sp.date}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          ₺{sp.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(sp.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <Wrench className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">{searchTerm ? "Arama sonucu bulunamadı" : "Henüz hizmet alışı yok"}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ServicePurchases;
